import moment from 'moment';
import { Api, Notification } from 'services';

export const formatFeeDate = (rawDate, outFormat = "D MMM YYYY") => {
    if (!rawDate) return "";
    const parsed = moment(
        rawDate,
        [
            "DD-MM-YYYY, hh:mm A",
            "DD-MM-YYYY, HH:mm",
            "DD-MM-YYYY hh:mm A",
            "DD-MM-YYYY",
            "YYYY-MM-DDTHH:mm:ss.SSSZ",
            "YYYY-MM-DDTHH:mm:ssZ",
            "YYYY-MM-DD",
            "MM-DD-YYYY",
            "D MMM YYYY",
            "YYYY/MM/DD",
            "DD/MM/YYYY"
        ],
        true
    );
    if (parsed.isValid()) return parsed.format(outFormat);
    const fallback = moment(rawDate);
    if (fallback.isValid()) return fallback.format(outFormat);
    return String(rawDate);
};

export const getResourceTypeBadge = (type) => {
    const t = String(type || "").toLowerCase();
    if (t === "testseries") {
        return { text: "Test series", bg: "#FFFBEB", border: "#FBBF24", color: "#D97706" };
    }
    if (t === "videoresource") {
        return { text: "Video resource", bg: "#F3E8FF", border: "#D8B4FE", color: "#7E22CE" };
    }
    if (t === "studymaterial") {
        return { text: "Study material", bg: "#EFF6FF", border: "#93C5FD", color: "#1D4ED8" };
    }
    return { text: "Classroom course", bg: "#F0F9FF", border: "#7DCDFF", color: "#008BE3" };
};

export const getInstallmentBadgeInfo = (inst) => {
    const rawStatus = (inst?.status || "").toLowerCase().trim();
    if (rawStatus === "paid" || rawStatus === "settled") {
        return { text: inst?.status || "Paid", bg: "#ECFDF5", border: "#34D399", textCol: "#059669" };
    }
    if (rawStatus === "refunded") {
        return { text: "Refunded", bg: "#EEF2FF", border: "#C7D2FE", textCol: "#4F46E5" };
    }
    if (rawStatus === "cancelled" || rawStatus === "rejected") {
        return { text: inst?.status || "Cancelled", bg: "#FEF2F2", border: "#F87171", textCol: "#DC2626" };
    }
    if (rawStatus === "overdue") {
        return { text: "Overdue", bg: "#FFFBEB", border: "#F59E0B", textCol: "#D97706" };
    }
    if (rawStatus.startsWith("due in") || rawStatus === "due today") {
        return { text: inst.status, bg: "#FFFBEB", border: "#FBBF24", textCol: "#D97706" };
    }
    if (rawStatus === "not requested") {
        return { text: "Not Requested", bg: "#F3F4F6", border: "#D1D5DB", textCol: "#6B7280" };
    }
    if (inst?.dueDate) {
        const dueMoment = moment(inst.dueDate);
        if (dueMoment.isValid()) {
            const today = moment().startOf('day');
            const diffDays = dueMoment.diff(today, 'days');
            if (diffDays < 0) {
                return { text: "Overdue", bg: "#FFFBEB", border: "#F59E0B", textCol: "#D97706" };
            } else if (diffDays === 0) {
                return { text: "Due today", bg: "#FFFBEB", border: "#FBBF24", textCol: "#D97706" };
            } else if (diffDays <= 30) {
                return { text: `Due in ${diffDays}d`, bg: "#FFFBEB", border: "#FBBF24", textCol: "#D97706" };
            } else {
                return { text: "Upcoming", bg: "#F0F9FF", border: "#7DCDFF", textCol: "#008BE3" };
            }
        }
    }
    return { text: inst?.status || "Pending", bg: "#FFFBEB", border: "#FBBF24", textCol: "#D97706" };
};

export const getInstallmentNumber = (inst, defaultNum) => {
    if (inst?.installmentNumber !== undefined && !isNaN(Number(inst.installmentNumber))) {
        return Number(inst.installmentNumber);
    }
    const match = String(inst?.name || '').match(/\b(\d+)(?:st|nd|rd|th)?\b/i);
    if (match) {
        return Number(match[1]);
    }
    return defaultNum;
};

export const handleDownloadInvoice = (item) => {
    let rawPaymentId = item?.invoiceNo || item?.invoice_no || item?.paymentId || item?.payment_id;
    if (!rawPaymentId && item?.installments && Array.isArray(item.installments)) {
        const paidInst = item.installments.find(i => (i?.status || '').toLowerCase() === 'paid' && (i?.invoiceNo || i?.paymentId));
        if (paidInst) {
            rawPaymentId = paidInst?.invoiceNo || paidInst?.paymentId;
        }
    }
    if (!rawPaymentId) {
        rawPaymentId = item?.transactionId || item?.id || item?.txnId;
    }
    const paymentId = Array.isArray(rawPaymentId) ? rawPaymentId[0] : rawPaymentId;
    if (!paymentId) {
        if (item?.invoiceUrl) {
            window.open(item.invoiceUrl, "_blank", "noopener,noreferrer");
            return;
        }
        Notification.error("Download invoice", "Invoice No / Payment ID not found for this installment.");
        return;
    }

    Notification.info("Download invoice", "Fetching invoice...");
    Api.get("/feev2/get-transaction-invoices")
        .params({ "paymentId[]": paymentId })
        .send((response, error) => {
            const extractUrl = (data) => {
                if (!data) return null;
                if (typeof data === "string" && (data.startsWith("http") || data.startsWith("//"))) return data;
                if (Array.isArray(data)) {
                    for (const el of data) {
                        const u = extractUrl(el);
                        if (u) return u;
                    }
                    return null;
                }
                if (typeof data === "object") {
                    if (data.url && typeof data.url === "string") return data.url;
                    if (data.invoiceUrl && typeof data.invoiceUrl === "string") return data.invoiceUrl;
                    if (data.file && typeof data.file === "string") return data.file;
                    if (data.pdf && typeof data.pdf === "string") return data.pdf;
                    if (data.link && typeof data.link === "string") return data.link;
                    if (data.downloadUrl && typeof data.downloadUrl === "string") return data.downloadUrl;
                    if (data[paymentId]) {
                        const u = extractUrl(data[paymentId]);
                        if (u) return u;
                    }
                    if (item?.invoiceNo && data[item.invoiceNo]) {
                        const u = extractUrl(data[item.invoiceNo]);
                        if (u) return u;
                    }
                    if (item?.transactionId && data[item.transactionId]) {
                        const u = extractUrl(data[item.transactionId]);
                        if (u) return u;
                    }
                    if (data.data) {
                        const u = extractUrl(data.data);
                        if (u) return u;
                    }
                    if (data.invoices) {
                        const u = extractUrl(data.invoices);
                        if (u) return u;
                    }
                    const invoiceKey = data.invoice || data.key || data.fileName || data.filename;
                    if (typeof invoiceKey === "string") {
                        if (invoiceKey.startsWith("http")) return invoiceKey;
                        return `${Api._s3_url}${invoiceKey.startsWith("/") ? invoiceKey.slice(1) : invoiceKey}`;
                    }
                }
                return null;
            };

            const invoiceUrl = extractUrl(response);

            if (invoiceUrl) {
                window.open(invoiceUrl, "_blank", "noopener,noreferrer");
            } else if (item?.invoiceUrl) {
                window.open(item.invoiceUrl, "_blank", "noopener,noreferrer");
            } else if (response?.show?.type === "error" || error) {
                Notification.error("Download invoice", response?.show?.message || error?.message || "Failed to download invoice.");
            } else {
                Notification.info("Download invoice", "Invoice request submitted. If not opened, check your browser's pop-up blocker.");
            }
        }, true);
};
